import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AutoRejectedResponse,
  ChangeSortInput,
  EndSortingInput,
  NextCardsInput,
  SaveSortingNoteInput,
  SortingCardsResponse,
  SortingDecisionRequest,
  SortingDecisionResponse,
  SortingOverview,
  SortingStartResponse,
  SortingSummary,
  SortingUndoResponse,
  SortOrder,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost } from '../../../lib/api-client';

// S15: SCR-01 and SCR-04. The API's realtime hints name `['sorting', jobId]`, so a
// colleague's decision recounts the hub's queue and this screen's progress.

export const SORTING_KEY = ['sorting'] as const;

export function sortingOverviewKey(jobId: string): readonly unknown[] {
  return [...SORTING_KEY, jobId, 'overview'];
}

function autoRejectedKey(jobId: string): readonly unknown[] {
  return [...SORTING_KEY, jobId, 'auto-rejected'];
}

/** JOB-10's "Start sorting (n)". */
export function useSortingOverview(jobId: string): UseQueryResult<SortingOverview, ApiError> {
  return useQuery<SortingOverview, ApiError>({
    queryKey: sortingOverviewKey(jobId),
    queryFn: () => apiGet<SortingOverview>(`/jobs/${jobId}/sorting`),
    placeholderData: (previous) => previous,
  });
}

/**
 * F8.01. Starting (or resuming) a session is what loading this screen means, so it
 * is the screen's query — fetched once per visit, never refetched behind the
 * recruiter's back: from here on the screen owns its queue.
 */
export function useSortingStart(jobId: string, only?: readonly string[]): UseQueryResult<SortingStartResponse, ApiError> {
  return useQuery<SortingStartResponse, ApiError>({
    queryKey: [...SORTING_KEY, jobId, 'session', only?.join(',') ?? 'all'],
    queryFn: () => apiPost<SortingStartResponse>(`/jobs/${jobId}/sorting`, only && only.length > 0 ? { onlyApplicationIds: [...only] } : {}),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

// --- The calls the session hook makes itself (it keeps its own optimistic queue) ---------

export function fetchCards(sessionId: string, input: NextCardsInput): Promise<SortingCardsResponse> {
  return apiPost<SortingCardsResponse>(`/sorting/sessions/${sessionId}/cards`, input);
}

export function changeSortOrder(sessionId: string, sortOrder: SortOrder, keep: readonly string[]): Promise<SortingCardsResponse> {
  return apiPatch<SortingCardsResponse>(`/sorting/sessions/${sessionId}`, { sortOrder, keep: [...keep] } satisfies ChangeSortInput);
}

export function postDecision(sessionId: string, input: SortingDecisionRequest): Promise<SortingDecisionResponse> {
  return apiPost<SortingDecisionResponse>(`/sorting/sessions/${sessionId}/decisions`, input);
}

export function postNote(sessionId: string, input: SaveSortingNoteInput): Promise<{ activityId: string | null }> {
  return apiPost<{ activityId: string | null }>(`/sorting/sessions/${sessionId}/notes`, input);
}

export function postUndo(sessionId: string, decisionId: string): Promise<SortingUndoResponse> {
  return apiPost<SortingUndoResponse>(`/sorting/sessions/${sessionId}/undo`, { decisionId });
}

export function postHeartbeat(sessionId: string, holding: readonly string[]): Promise<unknown> {
  return apiPost(`/sorting/sessions/${sessionId}/heartbeat`, { holding });
}

export async function endSession(sessionId: string, input: EndSortingInput): Promise<SortingSummary> {
  return (await apiPost<{ summary: SortingSummary }>(`/sorting/sessions/${sessionId}/end`, input)).summary;
}

/**
 * Leaving by closing the tab or following a link elsewhere. A beacon survives the
 * page going away where a fetch may not; if it is lost, the open session simply
 * resumes next time (S15), so nothing depends on it arriving.
 */
export function endSessionBeacon(sessionId: string, input: EndSortingInput): void {
  const body = new Blob([JSON.stringify(input)], { type: 'application/json' });
  navigator.sendBeacon(`/api/v1/sorting/sessions/${sessionId}/end`, body);
}

// --- SCR-04 --------------------------------------------------------------------------------

export function useAutoRejected(jobId: string): UseQueryResult<AutoRejectedResponse, ApiError> {
  return useQuery<AutoRejectedResponse, ApiError>({
    queryKey: autoRejectedKey(jobId),
    queryFn: () => apiGet<AutoRejectedResponse>(`/jobs/${jobId}/auto-rejected`),
    placeholderData: (previous) => previous,
  });
}

/** F8.12: the row leaves the list at once; a refusal puts it back (§5a rule 1). */
export function useRestoreApplication(jobId: string) {
  const queryClient = useQueryClient();
  const key = autoRejectedKey(jobId);
  return useMutation<unknown, ApiError, { readonly applicationId: string; readonly version: number }, { readonly previous: AutoRejectedResponse | undefined }>({
    mutationFn: ({ applicationId, version }) => apiPost(`/applications/${applicationId}/restore`, { version }),
    onMutate: async ({ applicationId }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AutoRejectedResponse>(key);
      if (previous) queryClient.setQueryData(key, { ...previous, rows: previous.rows.filter((row) => row.applicationId !== applicationId) });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [...SORTING_KEY, jobId] });
    },
  });
}
